import {
  useFonts,
  Poppins_400Regular,
  Poppins_500Medium,
  Poppins_600SemiBold,
} from "@expo-google-fonts/poppins";
import { Inter_400Regular } from "@expo-google-fonts/inter";
// Tobias loaded via local OTF files below
import { DMMono_400Regular } from "@expo-google-fonts/dm-mono";
import {
  DMSans_400Regular,
  DMSans_500Medium,
  DMSans_700Bold,
} from "@expo-google-fonts/dm-sans";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Text,
  View,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Application from "expo-application";
import { registerDevMenuItems } from "expo-dev-menu";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { PostHogProvider, usePostHog } from "posthog-react-native";
import {
  getStateFromPath as getDefaultStateFromPath,
  NavigationContainer,
  useNavigation,
  useNavigationContainerRef,
  type DocumentTitleOptions,
  type LinkingOptions,
  type NavigatorScreenParams,
} from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { useCallback, useEffect, useRef, useState } from "react";
import { OnboardingProvider } from "./onboarding/OnboardingContext";
import { EMBER } from "./onboarding/theme";
import { AuthGateProvider } from "./main/AuthGateContext";
import { AiChatProvider } from "./main/hooks/useAiChat";
import { QueryProvider } from "@src/packages/api/QueryProvider";
import { PushTokenRegistrar } from "@src/packages/notifications/PushTokenRegistrar";
import { SavedClinicsProvider } from "@louise/providers/hooks/useSavedClinics";
import { FeedbackProvider, ToastProvider } from "@louise/moon-ui";
import { ErrorBoundary } from "./error/ErrorBoundary";
import StytchProviderNative from "./utils/StytchProvider";
import StripeRootProvider from "./utils/StripeRootProvider";
import { useStytch, useStytchUser, useIsAuthReady } from "./utils/useStytch";
import { useAcquisitionCapture } from "./utils/useAcquisitionCapture";
import MainTabNavigator from "./main/MainTabNavigator";
import { StartTreatmentControllerProvider } from "./main/StartTreatmentController";
import OnboardingNavigator from "./onboarding/OnboardingNavigator";
import { getOnboardingFixtureScenario } from "./onboarding/onboardingFixture";
import YouNavigator from "./main/screens/YouScreen";
import { PlanBuilderNavigator } from "@src/packages/homepage/plan/builder";
import type { OnboardingStackParamList } from "./onboarding/types";
import type { MainTabParamList, YouStackParamList } from "./main/types";
import AuthCallbackScreen from "./main/screens/AuthCallbackScreen";
import { OnboardingSaveBoot } from "./main/OnboardingSaveBoot";
import PartnerInviteScreen from "./main/screens/PartnerInviteScreen";
import BetaInviteScreen from "./betaInvite/BetaInviteScreen";
import PeerInviteLandingScreen from "./peerInvite/PeerInviteLandingScreen";
import HomepageCanvasScreen from "./main/screens/HomepageCanvasScreen";
import MedicalHistoryCanvasScreen from "./main/screens/MedicalHistoryCanvasScreen";
import TwoFAChallengeScreen from "./main/screens/TwoFAChallengeScreen";
import {
  factorDeliveryMethod,
  useNeeds2FA,
} from "@src/packages/auth/hooks/use2FA";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { BetaInviteProvider } from "./betaInvite/BetaInviteContext";
import { ReferralProvider } from "./referral/ReferralContext";
import { ChatwootSessionSync } from "@louise/chatwoot";
import { PatientSyncProvider } from "@src/packages/patients/sync/usePatientSync";
import { GrabRoot } from "@src/packages/utils/grab";
import DevNavigator from "./main/screens/dev/DevNavigator";
import { I18nProvider, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { BadgeCelebrationProvider } from "@src/packages/badges/BadgeCelebrationProvider";
import BadgeCelebrationOverlay from "./main/components/BadgeCelebrationOverlay";
import { getBadgeDefinition } from "./main/screens/YouScreen/data";
import { isDevCareTeamReview } from "./main/dev/devCareTeamReview";
import { logoutWithRecovery } from "./utils/logoutWithRecovery";

export type RootStackParamList = {
  Onboarding: NavigatorScreenParams<OnboardingStackParamList> | undefined;
  Home: NavigatorScreenParams<MainTabParamList> | undefined;
  Profile: NavigatorScreenParams<YouStackParamList> | undefined;
  AuthCallback:
    { token?: string; stytch_token_type?: string; status?: string } | undefined;
  TwoFAChallenge: undefined;
  PlanBuilder: undefined;
  HomepageCanvas: undefined;
  MedicalHistoryCanvas: undefined;
  PartnerInvite: { code?: string; name?: string } | undefined;
  BetaInvite: { code?: string } | undefined;
  PeerInvite: { code?: string } | undefined;
  DevNavigator:
    | NavigatorScreenParams<
        import("./main/screens/dev/DevNavigator").DevStackParamList
      >
    | undefined;
};

const Stack = createNativeStackNavigator<RootStackParamList>();

const DEV_PLAN_BUILDER_ENABLED =
  __DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true";

const careHeroGlowGradient =
  require("./assets/images/care-hero-glow-gradient.png") as number;

const PAGE_TITLES: Record<string, string> = {
  // Root
  Onboarding: "Welcome",
  Home: "Home",
  Profile: "Your Profile",
  AuthCallback: "Signing In",
  PlanBuilder: "Plan Builder",
  HomepageCanvas: "Homepage Canvas",
  MedicalHistoryCanvas: "Medical History Canvas",
  PartnerInvite: "Partner Invitation",
  BetaInvite: "Welcome to Moon",
  PeerInvite: "You've been invited",
  DevNavigator: "Dev",
  DevHome: "Dev Components",
  DevComingSoon: "Coming Soon Components",
  DevComingSoonInventory: "Coming Soon Inventory",
  DevCoverageCardFixture: "Coverage Card Fixture",
  DevFilterChipAudit: "Filter Chip Audit",
  DevButtonAudit: "Button Audit",

  // Onboarding
  IntroSplash: "Welcome",
  Login: "Log in",
  WelcomeGoals: "Welcome",
  JourneyStage: "Your Journey",
  Sex: "About You",
  Birthday: "About You",
  Location: "Your Location",
  CreateAccount: "Create Account",
  PartnerJourneyConfirm: "Joining Your Partner",

  // Main Tabs
  Care: "Your Care",
  Costs: "Costs & Coverage",
  Access: "Access & Support",
  Learn: "Learn",
  Chat: "Chat",

  // Care Stack
  CareOverview: "Your Care",
  CycleDetail: "Cycle Detail",
  PastCycleDetail: "Past Cycle",
  Documents: "Documents",
  CryoVault: "Cryo Vault",
  EmbryoReport: "Embryo Testing",
  AllLabResults: "Lab Results",
  MedicalHistory: "Medical History",
  BasicInfoFlow: "Basic Info",
  InfertilityFlow: "Infertility History",
  FertilityTestingFlow: "Fertility Testing",
  FertilityTreatmentsFlow: "Fertility Treatments",
  MedicationsVaccinesFlow: "Medications & Vaccines",
  AllergiesFlow: "Allergies",
  SurgeriesFlow: "Surgeries",
  ExposureFlow: "Exposures",
  SubstancesFlow: "Substances",
  DietSupplementsFlow: "Diet & Supplements",
  MenstrualHealthFlow: "Menstrual Health",
  DiseaseHistoryFlow: "Disease History",
  SexualHealthFlow: "Sexual Health",
  ContraceptionFlow: "Contraception",
  PregnanciesFlow: "Pregnancies",
  SleepActivityFlow: "Sleep & Activity",

  // Costs Stack
  CostsOverview: "Costs & Coverage",
  Insurance: "Insurance",
  EmployerBenefits: "Employer Benefits",
  Financing: "Financing Options",
  Grants: "Grants",
  GrantDetail: "Grant Detail",
  TaxDeductions: "Tax Deductions",
  TaxReview: "Tax Review",

  // Access Stack
  AccessOverview: "Access & Support",
  FindCare: "Find Care",
  ClinicDetail: "Clinic Detail",
  Testing: "Testing",
  TestDetail: "Test",
  Programs: "Programs",
  ProgramDetail: "Program Detail",
  Supplements: "Supplements",
  SupplementDetail: "Supplement Detail",
  DonorRegistry: "Donor Registry",
  Therapies: "Therapies",
  TherapyDetail: "Therapy Detail",

  // Chat Stack
  Assistant: "Chat",
  ArticleDetail: "Article",
  Medications: "Medications",
  MedicationDetail: "Medication Detail",
  Conditions: "Conditions",
  Tests: "Tests",
  Procedures: "Procedures",

  // You Stack
  YouOverview: "Your Profile",
  NotificationsSettings: "Notifications",
  IntegrationsSettings: "Integrations",
  PreferencesSettings: "Preferences",
  AccountSettings: "Account Settings",
  PersonalInfoDetail: "Personal Info",
  MedicalFertilityDetail: "Medical & Fertility",
  CoverageBenefitsDetail: "Coverage & Benefits",
  ConnectionsDetail: "Connections",
  ResearchStudiesDetail: "Research Studies",
};

const documentTitle: DocumentTitleOptions = {
  formatter: (options, route) => {
    const screenTitle =
      options?.title ?? PAGE_TITLES[route?.name ?? ""] ?? route?.name;
    return screenTitle ? `${screenTitle} · Louise` : "Louise";
  },
};

const navigationTheme = {
  dark: false,
  colors: {
    primary: "#D4A574",
    background: "transparent",
    card: "transparent",
    text: "#2C2521",
    border: "#EBEBEB",
    notification: "#D4A574",
  },
  fonts: {
    regular: {
      fontFamily: "DMSans_400Regular",
      fontWeight: "400" as const,
    },
    medium: {
      fontFamily: "DMSans_500Medium",
      fontWeight: "500" as const,
    },
    bold: {
      fontFamily: "DMSans_500Medium",
      fontWeight: "700" as const,
    },
    heavy: {
      fontFamily: "DMSans_500Medium",
      fontWeight: "900" as const,
    },
  },
} as const;

const linking: LinkingOptions<RootStackParamList> = {
  prefixes: [
    "http://localhost:8082",
    "http://localhost:8086",
    "http://localhost:8087",
    "https://my.louise.life",
    "https://my.dev.louise.life",
    "mylouise://",
  ],
  getStateFromPath(path, options) {
    const state = getDefaultStateFromPath(path, options);
    const fixture = getOnboardingFixtureScenario(path);
    if (!state || !fixture) return state;

    const onboardingRoute = state.routes.find(
      (route) => route.name === "Onboarding",
    );
    const linkedOnboardingState = onboardingRoute?.state;
    if (!linkedOnboardingState) return state;

    const linkedRoute =
      linkedOnboardingState.routes[
        linkedOnboardingState.index ?? linkedOnboardingState.routes.length - 1
      ];

    return {
      ...state,
      routes: state.routes.map((route) => {
        if (route.name !== "Onboarding") return route;

        return {
          ...route,
          state: {
            ...linkedOnboardingState,
            index: fixture.routes.length - 1,
            routes: fixture.routes.map((fixtureRoute, index) => ({
              name: fixtureRoute.name,
              params: {
                ...(index === fixture.routes.length - 1
                  ? linkedRoute?.params
                  : undefined),
                ...fixtureRoute.params,
              },
            })),
          },
        };
      }),
    };
  },
  config: {
    screens: {
      Onboarding: {
        path: "welcome",
        screens: {
          IntroSplash: "",
          Login: {
            // Login shares the onboarding visual navigator, but it is a
            // first-class public destination rather than a child URL of
            // Welcome. Keep the branch-only legacy path as an input alias
            // while always generating the canonical `/login` URL.
            path: "login",
            exact: true,
            alias: [{ path: "welcome/login", exact: true }],
          },
          WelcomeGoals: "onboarding/goals",
          FamilyBuildingPath: "onboarding/family-building-path",
          JourneyStage: "onboarding/journey/:stage",
          TreatmentType: "onboarding/treatment-type/:stage",
          HowAreYouHelping: "onboarding/how-are-you-helping",
          WorkingWithSurrogate: "onboarding/working-with-surrogate",
          Sex: "onboarding/sex",
          Birthday: "onboarding/birthday",
          Location: "onboarding/location",
          CreateAccount: "onboarding/account",
          PartnerJourneyConfirm: "onboarding/partner-journey",
        },
      },
      Home: {
        screens: {
          Home: "home",
          Care: {
            path: "care",
            screens: {
              CareOverview: "",
              Documents: "documents",
              CycleDetail: "cycle-detail",
              MetricDetail: "metric/:metricKey",
              PastCycleDetail: "past-cycle/:cycleId",
              AllPastCycles: "past-cycles",
              CryoVault: "cryo-vault",
              EmbryoReport: "embryo-report/:cycleId",
              AllLabResults: "lab-results",
              MedicalHistory: "medical-history",
              BasicInfoFlow: "medical-history/basic-info",
              InfertilityFlow: "medical-history/infertility",
              FertilityTestingFlow: "medical-history/fertility-testing",
              FertilityTreatmentsFlow: "medical-history/fertility-treatments",
              MedicationsVaccinesFlow: "medical-history/medications-vaccines",
              AllergiesFlow: "medical-history/allergies",
              SurgeriesFlow: "medical-history/surgeries",
              ExposureFlow: "medical-history/exposure",
              SubstancesFlow: "medical-history/substances",
              DietSupplementsFlow: "medical-history/diet-supplements",
              MenstrualHealthFlow: "medical-history/menstrual-health",
              DiseaseHistoryFlow: "medical-history/disease-history",
              SexualHealthFlow: "medical-history/sexual-health",
              ContraceptionFlow: "medical-history/contraception",
              PregnanciesFlow: "medical-history/pregnancies",
              SleepActivityFlow: "medical-history/sleep-activity",
            },
          },
          Costs: {
            path: "costs",
            screens: {
              CostsOverview: "",
              Payments: "payments",
              Insurance: "insurance",
              EmployerBenefits: "employer-benefits",
              Financing: "financing",
              Grants: "grants",
              GrantDetail: "grants/:grantId",
              TaxDeductions: "tax-deductions",
              TaxReview: "tax-review",
              Glossary: "glossary",
            },
          },
          Access: {
            path: "access",
            screens: {
              AccessOverview: "",
              FindCare: "find-care",
              ClinicDetail: "clinic/:clinicId",
              Testing: "testing",
              TestProductDetail: "test/:testId/products/:productId",
              AmhComparison: "test/amh",
              TestComparison: "test/measurement/:testId",
              TestPanelComparison: "test/panel/:testId",
              TestDetail: "test/:testId",
              Programs: "programs",
              ProgramDetail: "program/:programId",
              ConditionDetail: "condition/:conditionId",
              Supplements: "supplements",
              SupplementDetail: "supplement/:supplementId",
              DonorRegistry: "donor-registry",
              Therapies: "therapies",
              TherapyDetail: "therapy/:therapyId",
              Pharmacy: "pharmacy",
              PharmacyDetail: "pharmacy/:pharmacyId",
            },
          },
          Learn: {
            path: "learn",
            screens: {
              Learn: "",
              ArticleDetail: "article/:articleId",
              Medications: "medications",
              MedicationDetail: "medication/:medicationId",
              Conditions: "conditions",
              ConditionDetail: "condition/:conditionId",
              Tests: "tests",
              TestDetail: "test/:testId",
              Procedures: "procedures",
              ProcedureDetail: "procedure/:procedureId",
            },
          },
          Chat: {
            path: "chat",
            screens: {
              Assistant: "",
            },
          },
        },
      },
      Profile: {
        path: "you",
        screens: {
          YouOverview: "",
          PersonalInfoDetail: "personal-info",
          MedicalFertilityDetail: "medical-fertility",
          CoverageBenefitsDetail: "coverage-benefits",
          ConnectionsDetail: "connections",
          ResearchStudiesDetail: "research-studies",
          NotificationsSettings: "notifications",
          IntegrationsSettings: "integrations",
          PreferencesSettings: "preferences",
          AccountSettings: "account",
        },
      },
      ...(DEV_PLAN_BUILDER_ENABLED
        ? {
            PlanBuilder: {
              path: "plan-builder",
              screens: {
                GoalRecap: "goal",
                ModalityScreen: "modality",
                AcceptedPlan: "accepted-plan",
                PastCycles: "past-cycles",
                PastCycleDetails: "past-cycle-details",
                Conditions: "conditions",
                Ethnicity: "ethnicity",
                PartnerStatus: "partner",
                ClinicStatus: "clinic",
                KnownResults: "results",
                LocationInsurance: "location",
                Timeline: "timeline",
                FreeText: "notes",
                Building: "building",
                PlanReveal: "reveal",
              },
            },
            HomepageCanvas: "canvas",
            MedicalHistoryCanvas: "medical-history-canvas",
          }
        : {}),
      AuthCallback: "auth/callback",
      PartnerInvite: "partner-invite",
      BetaInvite: "beta",
      PeerInvite: "invite/:code?",
      DevNavigator: {
        path: "dev",
        screens: {
          DevHome: "",
          DevComingSoon: "coming-soon",
          DevComingSoonInventory: "coming-soon-inventory",
          DevCoverageCardFixture: "coverage-card",
          DevFieldPlayground: "field-playground",
          DevFilterChipAudit: "filter-chip-audit/:variant",
          DevButtonAudit: "button-audit/:variant",
          DevPharmacyQuoteClaims: "pharmacy-quote-claims",
        },
      },
    },
  },
};

/**
 * Which deployment this session belongs to, sent on every event. `__DEV__`
 * only disables session replay, not capture, so without this property local
 * and preview sessions are indistinguishable from production in PostHog
 * (they were 91% of all volume when this was added — see #3470).
 */
function analyticsEnvironment(): "production" | "dev" | "preview" | "local" {
  if (__DEV__) return "local";
  if (Platform.OS === "web" && typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "my.louise.life") return "production";
    if (host === "my.dev.louise.life") return "dev";
    if (host === "localhost" || host === "127.0.0.1") return "local";
    return "preview";
  }
  return "production";
}

// Production traffic goes to the "Louise Patient" PostHog project; every
// other environment goes to "Louise Patient (dev)" so dev/preview sessions
// never pollute production analytics. Keys come from env only — the dev key
// from the committed .env, the prod key solely from production build
// environments (Vercel project env, EAS production environment) — never from
// code defaults: a build with a missing var must capture nothing rather than
// capture into the wrong project.
const POSTHOG_PROD_API_KEY = process.env.EXPO_PUBLIC_POSTHOG_PROD_API_KEY ?? "";
const POSTHOG_DEV_API_KEY = process.env.EXPO_PUBLIC_POSTHOG_API_KEY ?? "";

function posthogApiKey(): string {
  return analyticsEnvironment() === "production"
    ? POSTHOG_PROD_API_KEY
    : POSTHOG_DEV_API_KEY;
}

function PostHogErrorBoundary({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog();
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        posthog.capture("app_error_caught", {
          error_message: error.message,
          error_name: error.name,
          component_stack: errorInfo.componentStack ?? "",
        });
        // Also feed PostHog's error-tracking product ($exception), which
        // groups by stack rather than by message.
        posthog.captureException(error, {
          source: "app_error_caught",
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

function useDevMenu() {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  useEffect(() => {
    // expo-dev-menu is native-only; registering items on web throws
    // "`expo-dev-menu` isn't supported on Expo Web."
    if (!__DEV__ || Platform.OS === "web") return;
    void registerDevMenuItems([
      {
        name: "Toggle Dev Components",
        callback: () => {
          const route = navigation.getState()?.routes.at(-1);
          if (route?.name === "DevNavigator") {
            navigation.goBack();
          } else {
            navigation.navigate("DevNavigator");
          }
        },
        shouldCollapse: true,
      },
    ]);
  }, [navigation]);
}

/**
 * Insurance belongs to the patient's coverage record, which is a separate API
 * resource from the core patient profile. Start that small request as soon as
 * an authenticated session is ready, so a profile dialog reuses warm query
 * data instead of looking as though it is checking something on open.
 */
function CoveragePreloader() {
  useCoverage();
  return null;
}

const AUTH_BOOTSTRAP_TIMEOUT_MS = 8_000;

function AuthBootstrapRecovery({
  onReload,
  onSignOut,
  isSigningOut,
}: {
  onReload: () => void;
  onSignOut: () => void;
  isSigningOut: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("AuthBootstrapRecovery");

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: EMBER.colors.bg,
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
      }}
    >
      <Text
        style={{
          color: EMBER.colors.text,
          fontFamily: "DMSans_700Bold",
          fontSize: 22,
          textAlign: "center",
          marginBottom: 12,
        }}
      >
        {t("auth.session_recovery.taking_longer_than_expected")}
      </Text>
      <Text
        style={{
          color: EMBER.colors.textLight,
          fontFamily: "DMSans_400Regular",
          fontSize: 15,
          lineHeight: 22,
          maxWidth: 420,
          textAlign: "center",
          marginBottom: 24,
        }}
      >
        {t("auth.session_recovery.session_setup_failed")}
      </Text>
      <View style={{ width: "100%", maxWidth: 320, gap: 12 }}>
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            capturePress(
              pressLabel(t("auth.session_recovery.reload")),
              "auth.session_recovery.reload",
            );
            onReload();
          }}
          style={{
            alignItems: "center",
            backgroundColor: EMBER.colors.warm,
            borderRadius: 999,
            paddingHorizontal: 24,
            paddingVertical: 14,
          }}
        >
          <Text
            style={{
              color: "#FFFFFF",
              fontFamily: "DMSans_700Bold",
              fontSize: 15,
            }}
          >
            {t("auth.session_recovery.reload")}
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          disabled={isSigningOut}
          onPress={() => {
            capturePress(
              pressLabel(t("auth.session_recovery.sign_out")),
              "auth.session_recovery.sign_out",
            );
            onSignOut();
          }}
          style={{
            alignItems: "center",
            borderColor: EMBER.colors.warm,
            borderRadius: 999,
            borderWidth: 1,
            opacity: isSigningOut ? 0.6 : 1,
            paddingHorizontal: 24,
            paddingVertical: 13,
          }}
        >
          {isSigningOut ? (
            <ActivityIndicator size="small" color={EMBER.colors.warm} />
          ) : (
            <Text
              style={{
                color: EMBER.colors.warm,
                fontFamily: "DMSans_700Bold",
                fontSize: 15,
              }}
            >
              {t("auth.session_recovery.sign_out")}
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
}

function RootNavigator() {
  useDevMenu();
  const { user } = useStytchUser();
  const devCareTeamReview = isDevCareTeamReview();
  const stytch = useStytch();
  const isAuthReady = useIsAuthReady();
  const posthog = usePostHog();
  useAcquisitionCapture();
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const sessionAuthUserId = useRef<string | null>(null);
  // Derive needs2FA from the same signals the backend gates on (enrollment
  // flag + session factor categories). When the user enters their TOTP code,
  // Stytch updates the session and this re-evaluates to false automatically
  // — no in-memory "verified" flag to keep in sync.
  const { needs: needs2FA, isLoading: is2FALoading } = useNeeds2FA();

  // Authenticate session once at the app level so individual useApiClient() hooks
  // don't each trigger their own authenticate() call.
  // Also persist the primary auth method so the login screen can show a "Last used" badge.
  const userId = user?.user_id;

  useEffect(() => {
    if (!userId) {
      sessionAuthUserId.current = null;
      return;
    }

    if (sessionAuthUserId.current === userId) return;
    sessionAuthUserId.current = userId;
    stytch.session
      .authenticate({
        session_duration_minutes: 60 * 24 * 30,
      })
      .then((resp) => {
        // factorDeliveryMethod also covers WebAuthn factors, which can
        // carry no delivery_method (passkey sign-in on web).
        const method = factorDeliveryMethod(
          resp.session.authentication_factors[0],
        );
        if (method) {
          AsyncStorage.setItem("louise_last_auth_method", method);
        }
      })
      .catch(() => {
        // Session auth failure is handled elsewhere; don't block on storage.
      });
  }, [stytch, userId]);

  const isBootstrapping = !isAuthReady || Boolean(user && is2FALoading);

  useEffect(() => {
    if (!isBootstrapping) {
      setBootstrapTimedOut(false);
      return;
    }

    const timeout = setTimeout(
      () => setBootstrapTimedOut(true),
      AUTH_BOOTSTRAP_TIMEOUT_MS,
    );
    return () => clearTimeout(timeout);
  }, [isBootstrapping]);

  // Identify by Stytch user ID so every event links back to a real user.
  // Phone-only signups have no email, so also pin phone and name — otherwise
  // those users are an opaque `user-live-…` id in PostHog. Keyed by user_id
  // only (not object identity): Stytch returns a fresh user object on every
  // session refresh, and re-identifying each time spams $set events. Identity
  // fields linked mid-session refresh on the next app start.
  const identifiedUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    // While Stytch is still restoring the session, `user` is transiently null.
    // Acting on it would reset() on every cold boot, wiping the stored
    // anonymous id and unlinking pre-signup activity from the later signup.
    if (!isAuthReady) return;
    if (user) {
      if (identifiedUserIdRef.current === user.user_id) return;
      identifiedUserIdRef.current = user.user_id;
      const email = user.emails?.[0]?.email;
      const phone = user.phone_numbers?.[0]?.phone_number;
      const name = [user.name?.first_name, user.name?.last_name]
        .filter(Boolean)
        .join(" ");
      posthog.identify(user.user_id, {
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ...(name ? { name } : {}),
      });
    } else {
      identifiedUserIdRef.current = null;
      // Reset only if this device still carries an identified distinct id
      // (sign-out, or a session that expired while the app was closed). The
      // SDK exposes no identified-state getter, but we only ever identify
      // with Stytch user ids ("user-…"), and the SDK's own anonymous ids are
      // plain UUIDs. getDistinctId() is "" until the SDK finishes loading its
      // persisted state, so wait for readiness — and re-check that no user
      // was identified in the meantime before wiping.
      void posthog.ready().then(() => {
        if (identifiedUserIdRef.current !== null) return;
        if (posthog.getDistinctId().startsWith("user-")) {
          posthog.reset();
        }
      });
    }
  }, [user, isAuthReady, posthog]);

  // Wait for Stytch session restore + 2FA status before deciding which screen
  if (isBootstrapping && !bootstrapTimedOut) {
    return null;
  }

  if (isBootstrapping) {
    const handleReload = () => {
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.location.reload();
      } else {
        setBootstrapTimedOut(false);
      }
    };
    const handleSignOut = () => {
      setIsSigningOut(true);
      void logoutWithRecovery({
        revokeRegisteredDevice: async () => {},
        revokeSession: () => stytch.session.revoke({ forceClear: true }),
      }).finally(() => {
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.location.reload();
        } else {
          setIsSigningOut(false);
          setBootstrapTimedOut(false);
        }
      });
    };

    return (
      <AuthBootstrapRecovery
        onReload={handleReload}
        onSignOut={handleSignOut}
        isSigningOut={isSigningOut}
      />
    );
  }

  return (
    <PushTokenRegistrar enabled={Boolean(user && !needs2FA)}>
      {user && !needs2FA ? <CoveragePreloader /> : null}
      {/* Rescue locally stored onboarding answers when auth completed without
          remounting CreateAccountScreen — e.g. native OAuth (#1393). */}
      {user && !needs2FA ? <OnboardingSaveBoot /> : null}
      <StartTreatmentControllerProvider>
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
            contentStyle: { backgroundColor: "transparent" },
          }}
        >
          {user && needs2FA ? (
            <>
              <Stack.Screen name="TwoFAChallenge">
                {() => <TwoFAChallengeScreen />}
              </Stack.Screen>
            </>
          ) : user || devCareTeamReview ? (
            <>
              <Stack.Screen name="Home" component={MainTabNavigator} />
              <Stack.Screen name="Onboarding" component={OnboardingNavigator} />
              <Stack.Screen
                name="Profile"
                component={YouNavigator}
                options={{ animation: "none" }}
              />
              {DEV_PLAN_BUILDER_ENABLED && (
                <Stack.Screen
                  name="PlanBuilder"
                  component={PlanBuilderNavigator}
                  options={{ animation: "slide_from_bottom" }}
                />
              )}
              <Stack.Screen
                name="AuthCallback"
                component={AuthCallbackScreen}
                options={{ headerShown: false }}
              />
              <Stack.Screen
                name="PartnerInvite"
                component={PartnerInviteScreen}
                options={{
                  headerShown: false,
                  title: "Partner Invitation — Louise",
                }}
              />
              <Stack.Screen
                name="BetaInvite"
                component={BetaInviteScreen}
                options={{
                  headerShown: false,
                  title: "Welcome to Moon — Louise",
                }}
              />
              <Stack.Screen
                name="PeerInvite"
                component={PeerInviteLandingScreen}
                options={{
                  headerShown: false,
                  title: "You've been invited — Louise",
                }}
              />
              {(__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") && (
                <Stack.Screen
                  name="HomepageCanvas"
                  component={HomepageCanvasScreen}
                  options={{ headerShown: false }}
                />
              )}
              {(__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") && (
                <Stack.Screen
                  name="MedicalHistoryCanvas"
                  component={MedicalHistoryCanvasScreen}
                  options={{ headerShown: false }}
                />
              )}
              {(__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") && (
                <Stack.Screen
                  name="DevNavigator"
                  component={DevNavigator}
                  options={{ headerShown: false }}
                />
              )}
            </>
          ) : (
            <>
              <Stack.Screen name="Onboarding" component={OnboardingNavigator} />
              <Stack.Screen name="Home" component={MainTabNavigator} />
              <Stack.Screen
                name="AuthCallback"
                component={AuthCallbackScreen}
                options={{ headerShown: false }}
              />
              <Stack.Screen
                name="PartnerInvite"
                component={PartnerInviteScreen}
                options={{
                  headerShown: false,
                  title: "Partner Invitation — Louise",
                }}
              />
              <Stack.Screen
                name="BetaInvite"
                component={BetaInviteScreen}
                options={{
                  headerShown: false,
                  title: "Welcome to Moon — Louise",
                }}
              />
              <Stack.Screen
                name="PeerInvite"
                component={PeerInviteLandingScreen}
                options={{
                  headerShown: false,
                  title: "You've been invited — Louise",
                }}
              />
              {(__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") && (
                <Stack.Screen
                  name="HomepageCanvas"
                  component={HomepageCanvasScreen}
                  options={{ headerShown: false }}
                />
              )}
              {(__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") && (
                <Stack.Screen
                  name="MedicalHistoryCanvas"
                  component={MedicalHistoryCanvasScreen}
                  options={{ headerShown: false }}
                />
              )}
              {(__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") && (
                <Stack.Screen
                  name="DevNavigator"
                  component={DevNavigator}
                  options={{ headerShown: false }}
                />
              )}
            </>
          )}
        </Stack.Navigator>
      </StartTreatmentControllerProvider>
      <BadgeCelebrationOverlay />
    </PushTokenRegistrar>
  );
}

/**
 * Owns the NavigationContainer so navigation can be reported to PostHog.
 * Autocapture's `captureScreens` can't do this: it hooks navigation from
 * inside the container, but PostHogProvider must wrap the whole tree (screens
 * capture events too), so it sits outside and never sees route changes.
 */
function AppNavigationContainer() {
  const posthog = usePostHog();
  const navigationRef = useNavigationContainerRef<RootStackParamList>();
  const currentScreenRef = useRef<string | null>(null);

  const captureCurrentScreen = useCallback(() => {
    if (!navigationRef.isReady()) return;
    const routeName = navigationRef.getCurrentRoute()?.name;
    // State changes on the same route (params-only updates) are not new
    // screen views.
    if (!routeName || routeName === currentScreenRef.current) return;
    currentScreenRef.current = routeName;
    // Super property so every event (button presses, API actions) carries the
    // screen it happened on, not just the $screen events themselves.
    void posthog.register({ $screen_name: routeName });
    posthog.screen(routeName);
  }, [navigationRef, posthog]);

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      theme={navigationTheme}
      documentTitle={documentTitle}
      onReady={captureCurrentScreen}
      onStateChange={captureCurrentScreen}
    >
      <RootNavigator />
    </NavigationContainer>
  );
}

export default function App() {
  const [loaded, fontError] = useFonts({
    Poppins_400Regular,
    Poppins_500Medium,
    Poppins_600SemiBold,
    Inter_400Regular,
    Tobias_400Regular: require("./assets/fonts/tobias/regular.otf") as string,
    Tobias_300Light: require("./assets/fonts/tobias/light.otf") as string,
    "Tobias-Thin": require("./assets/fonts/tobias/thin.otf") as string,
    DMMono_400Regular,
    DMSans_400Regular,
    DMSans_500Medium,
    DMSans_700Bold,
  });
  const [fontLoadTimedOut, setFontLoadTimedOut] = useState(false);

  useEffect(() => {
    if (loaded || fontError) {
      setFontLoadTimedOut(false);
      return;
    }

    const timeout = setTimeout(() => setFontLoadTimedOut(true), 8_000);
    return () => clearTimeout(timeout);
  }, [fontError, loaded]);

  if (Platform.OS === "web" && typeof document !== "undefined") {
    // Latch the test-harness flag at app entry. Multi-step flows (onboarding)
    // navigate between routes and drop the ?test=1 query string, which would
    // otherwise hide the hidden test sign-in/up form on later screens. Capture
    // it once here so useTestSignIn can read it for the whole browser session.
    // Still hard-gated downstream by the TEST-project Stytch token prefix.
    try {
      if (new URLSearchParams(window.location.search).get("test") === "1") {
        window.sessionStorage?.setItem("louise_test_mode", "1");
      }
    } catch {
      // sessionStorage may be unavailable (private mode); ignore.
    }

    const id = "expo-focus-reset";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent =
        "[data-focusvisible-polyfill], *:focus { outline: none !important; }";
      document.head.appendChild(style);
    }

    // SEO meta tags
    if (!document.querySelector('meta[name="description"]')) {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Louise helps you navigate fertility care — track treatment, manage costs, find clinics, and stay informed.";
      document.head.appendChild(meta);
    }
    if (!document.querySelector('meta[property="og:title"]')) {
      const ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      ogTitle.content = "Louise — Your Fertility Care Companion";
      document.head.appendChild(ogTitle);
    }
    if (!document.querySelector('meta[property="og:description"]')) {
      const ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      ogDesc.content =
        "Track your fertility treatment, manage costs, find top clinics, and access personalized health insights.";
      document.head.appendChild(ogDesc);
    }
    if (!document.querySelector('meta[property="og:type"]')) {
      const ogType = document.createElement("meta");
      ogType.setAttribute("property", "og:type");
      ogType.content = "website";
      document.head.appendChild(ogType);
    }
  }

  if (!loaded && !fontError && !fontLoadTimedOut) {
    if (Platform.OS === "web") {
      return (
        <View
          style={{
            flex: 1,
            backgroundColor: "#FFFFFF",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ActivityIndicator size="small" color="#D4A574" />
        </View>
      );
    }
    return null;
  }

  return (
    <GrabRoot>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <I18nProvider>
          <PostHogProvider
            apiKey={posthogApiKey()}
            options={{
              host: "https://eu.i.posthog.com",
              enableSessionReplay: !__DEV__,
              customAppProperties: (properties) => ({
                ...properties,
                app_platform: Platform.OS,
                environment: analyticsEnvironment(),
                api_base_url: process.env.EXPO_PUBLIC_API_BASE_URL,
                ...(Platform.OS === "web" && typeof window !== "undefined"
                  ? { domain: window.location.hostname }
                  : {
                      app_id: Application.applicationId ?? "",
                      app_version: Application.nativeApplicationVersion ?? "",
                    }),
              }),
            }}
            autocapture={{
              captureScreens: false,
              captureTouches: true,
            }}
          >
            <StytchProviderNative>
              <BetaInviteProvider>
                <ReferralProvider>
                  <OnboardingProvider>
                    <AuthGateProvider>
                      <QueryProvider>
                        <FeedbackProvider>
                          <SavedClinicsProvider>
                            <SafeAreaProvider>
                              <ToastProvider
                                backgroundSource={careHeroGlowGradient}
                              >
                                <BottomSheetModalProvider>
                                  <PostHogErrorBoundary>
                                    <ChatwootSessionSync />
                                    <PatientSyncProvider>
                                      <StripeRootProvider>
                                        <BadgeCelebrationProvider
                                          isBadgeSupported={(badgeId) =>
                                            getBadgeDefinition(badgeId) !== null
                                          }
                                        >
                                          <AiChatProvider>
                                            <AppNavigationContainer />
                                          </AiChatProvider>
                                        </BadgeCelebrationProvider>
                                      </StripeRootProvider>
                                    </PatientSyncProvider>
                                  </PostHogErrorBoundary>
                                </BottomSheetModalProvider>
                              </ToastProvider>
                            </SafeAreaProvider>
                          </SavedClinicsProvider>
                        </FeedbackProvider>
                      </QueryProvider>
                    </AuthGateProvider>
                  </OnboardingProvider>
                </ReferralProvider>
              </BetaInviteProvider>
            </StytchProviderNative>
          </PostHogProvider>
        </I18nProvider>
      </GestureHandlerRootView>
    </GrabRoot>
  );
}
